"use client";

import { useState, useEffect } from "react";
import { MoonStar, SunMedium } from "lucide-react";

/**
 * DarkModeToggle — Inline (non-floating) dark/light toggle for use in navbars.
 * Renders as a compact button that fits alongside nav links.
 */
export default function DarkModeToggle() {
  const [isDark, setIsDark] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      // Check localStorage first, then system preference
      const stored = localStorage.getItem("theme");
      if (stored === "dark") {
        setIsDark(true);
      } else if (stored === "light") {
        setIsDark(false);
      } else {
        // No stored preference — use system preference
        setIsDark(window.matchMedia("(prefers-color-scheme: dark)").matches);
      }
      setMounted(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark, mounted]);

  if (!mounted) return null;

  return (
    <button
      onClick={() => setIsDark((prev) => !prev)}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="relative w-12 h-12 rounded-2xl flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {/* Sun icon — shown in dark mode */}
      <SunMedium
        className={`absolute top-1/2 left-1/2 h-[23px] w-[23px] -translate-x-1/2 -translate-y-1/2 text-[#E2BF59] transition-all duration-300 ${
          isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-75"
        }`}
        strokeWidth={2.35}
      />

      {/* Moon icon — shown in light mode */}
      <MoonStar
        className={`h-[23px] w-[23px] text-gray-700 dark:text-gray-300 transition-all duration-300 ${
          isDark ? "opacity-0 -rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
        }`}
        strokeWidth={2.35}
      />
    </button>
  );
}
